import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { buildWeeklyMonthSubtotals } from '../../lib/PlannerWeeklyView'
import {
  formatPlannerEditableDisplayValue,
  getPlannerDisplayDecimals,
  getPlannerEditableRawValue,
  hasPlannerValueChanged,
  normalizePlannerRawInput,
  parsePlannerEditedValue,
} from '../../lib/PlannerEditableCell'

/**
 * Weekly planning grid that mirrors the structure/labels from:
 * frontend/temp/fresh-start/components/retail-planning/planning-grid.tsx
 *
 * This component is presentational and receives already-shaped per-week data.
 *
 * Props:
 *  - data: array of period objects for each week column
 *  - onChange: (weekIndex, field, value, segmentMeta?) => void for editable numeric fields
 */
function WeeklyPlanningGrid({ data, onChange, onCopyActuals }) {
  const monthPalette = ['#f3e8ff', '#dbeafe', '#dcfce7', '#e0e7ff', '#fef3c7', '#fee2e2']
  const editableFields = new Set(['salesPlan', 'planInvBOM', 'markdownPlan', 'planPercent', 'markdownPlanPercent'])
  const [inventoryOnOrderExpanded, setInventoryOnOrderExpanded] = useState(false)
  const [showAllMonthColumns, setShowAllMonthColumns] = useState(false)
  const [editValues, setEditValues] = useState({})

  const renderMonthLabel = (label, sx = {}) => {
    const match = typeof label === 'string' && label.match(/^(.+?)\s+(\d+\s*\/\s*\d+)$/)
    if (match) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2, ...sx }}>
          <span>{match[1]}</span>
          <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.85 }}>{match[2]}</span>
        </Box>
      )
    }
    return label
  }

  const formatFreshnessPercent = (value) => {
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    return `${Math.round(n * 100)}%`
  }

  const safeNumber = (value, fallback = 0) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }

  const contributionShare = (segment, segmentCount) => {
    const days = safeNumber(segment?.days, 0)
    if (days > 0) return days
    return segmentCount > 0 ? (1 / segmentCount) : 1
  }

  const buildColumnFieldValue = ({ row, segment, field }) => {
    const hasSegment = !!segment
    const segments = Array.isArray(row?.monthSegments) ? row.monthSegments : []
    const rawTotal = segments.reduce((acc, currentSegment) => acc + contributionShare(currentSegment, segments.length), 0)
    const share = hasSegment
      ? ((rawTotal > 0 ? contributionShare(segment, segments.length) / rawTotal : (segments.length ? 1 / segments.length : 1)))
      : 1

    const segmentSalesPlan = hasSegment ? safeNumber(segment?.salesPlan, safeNumber(row?.salesPlan) * share) : safeNumber(row?.salesPlan)
    const segmentMarkdownPlan = hasSegment ? safeNumber(segment?.markdownPlan, safeNumber(row?.markdownPlan) * share) : safeNumber(row?.markdownPlan)
    const segmentPlanInvBOM = hasSegment ? safeNumber(segment?.planInvBOM, safeNumber(row?.planInvBOM)) : safeNumber(row?.planInvBOM)
    const segmentActualInvBOM = hasSegment ? safeNumber(segment?.actualInvBOM, safeNumber(row?.actualInvBOM)) : safeNumber(row?.actualInvBOM)
    const segmentSalesActual = hasSegment ? safeNumber(segment?.salesActual, safeNumber(row?.salesActual) * share) : safeNumber(row?.salesActual)
    const segmentMarkdownActual = hasSegment ? safeNumber(segment?.markdownActual, safeNumber(row?.markdownActual) * share) : safeNumber(row?.markdownActual)
    const segmentPlanInflowRetail = hasSegment ? safeNumber(row?.planInflowRetail) * share : safeNumber(row?.planInflowRetail)
    const segmentPlanInflowCost = hasSegment ? safeNumber(row?.planInflowCost) * share : safeNumber(row?.planInflowCost)
    const segmentActualInflowRetail = hasSegment ? safeNumber(row?.actualInflowRetail) * share : safeNumber(row?.actualInflowRetail)
    const segmentActualInflowCost = hasSegment ? safeNumber(row?.actualInflowCost) * share : safeNumber(row?.actualInflowCost)

    if (field === 'salesActual') return segmentSalesActual
    if (field === 'salesPlan') return segmentSalesPlan
    if (field === 'planPercent') return row?.[field]
    if (field === 'actualPercent') return row?.[field]
    if (field === 'actualInvBOM') return segmentActualInvBOM
    if (field === 'actualSS') return segmentSalesActual ? (segmentActualInvBOM / segmentSalesActual) : 0
    if (field === 'freshness') return row?.[field] ?? null
    if (field === 'trendedTurn') return row?.trendedTurn ?? null
    if (field === 'planSS') return segmentSalesPlan ? (segmentPlanInvBOM / segmentSalesPlan) : 0
    if (field === 'planInvBOM') return segmentPlanInvBOM
    if (field === 'planInflowRetail') return segmentPlanInflowRetail
    if (field === 'planInflowCost') return segmentPlanInflowCost
    if (field === 'onOrderRetailMtd') return hasSegment ? safeNumber(segment?.onOrderRetailMtd, safeNumber(row?.onOrderRetailMtd) * share) : safeNumber(row?.onOrderRetailMtd)
    if (field === 'onOrderCostMtd') return hasSegment ? safeNumber(segment?.onOrderCostMtd, safeNumber(row?.onOrderCostMtd) * share) : safeNumber(row?.onOrderCostMtd)
    if (field === 'actualInflowRetail') return segmentActualInflowRetail
    if (field === 'actualInflowCost') return segmentActualInflowCost
    if (field === 'markdownActual') return segmentMarkdownActual
    if (field === 'markdownPlan') return segmentMarkdownPlan
    if (field === 'markdownPlanPercent') return row?.[field]
    if (field === 'markdownActualPercent') return row?.[field]

    return row?.[field]
  }

  const monthTotals = useMemo(() => {
    const totals = new Map()
    const rows = Array.isArray(data) ? data : []

    rows.forEach((row, idx) => {
      const monthKey = String(row?.representativeMonthKey || `month-${idx}`)
      const rowSegments = Array.isArray(row?.monthSegments) ? row.monthSegments : []

      if (rowSegments.length > 1) {
        rowSegments.forEach((segment) => {
          const segmentMonthKey = String(segment?.monthKey || monthKey)
          const actualShare = contributionShare(segment, rowSegments.length)
          const rawTotal = rowSegments.reduce((acc, currentSegment) => acc + contributionShare(currentSegment, rowSegments.length), 0)
          const normalizedShare = rawTotal > 0 ? (actualShare / rawTotal) : (1 / rowSegments.length)
          const segmentSalesPlan = safeNumber(segment?.salesPlan, safeNumber(row?.salesPlan) * normalizedShare)
          const segmentSalesActual = safeNumber(row?.salesActual) * normalizedShare

          const segmentMarkdownPlan = safeNumber(segment?.markdownPlan, safeNumber(row?.markdownPlan) * normalizedShare)
          const segmentMarkdownActual = safeNumber(segment?.markdownActual, safeNumber(row?.markdownActual) * normalizedShare)

          if (!totals.has(segmentMonthKey)) totals.set(segmentMonthKey, { salesPlan: 0, salesActual: 0, markdownPlan: 0, markdownActual: 0 })
          const current = totals.get(segmentMonthKey)
          current.salesPlan += segmentSalesPlan
          current.salesActual += segmentSalesActual
          current.markdownPlan += segmentMarkdownPlan
          current.markdownActual += segmentMarkdownActual
        })
        return
      }

      if (!totals.has(monthKey)) totals.set(monthKey, { salesPlan: 0, salesActual: 0, markdownPlan: 0, markdownActual: 0 })
      const current = totals.get(monthKey)
      current.salesPlan += safeNumber(row?.salesPlan)
      current.salesActual += safeNumber(row?.salesActual)
      current.markdownPlan += safeNumber(row?.markdownPlan)
      current.markdownActual += safeNumber(row?.markdownActual)
    })

    return totals
  }, [data])

  const baseColumns = useMemo(() => {
    const rows = Array.isArray(data) ? data : []

    return rows.flatMap((row, idx) => {
      const rowSegments = Array.isArray(row?.monthSegments) ? row.monthSegments : []
      const splitSegments = rowSegments.length > 1 ? rowSegments : []

      if (!splitSegments.length) {
        return [{
          type: 'week',
          displayKey: `week-${row?.period ?? idx}`,
          key: row?.period ?? String(idx),
          label: row?.headerLabel ?? row?.shortPeriod ?? `W${idx + 1}`,
          monthKey: row?.representativeMonthKey || `month-${idx}`,
          monthLabel: row?.representativeMonthLabel || 'Month',
          monthDayCount: Number(row?.representativeMonthDayCount ?? 0),
          isPartialWeekInMonth: !!row?.isPartialWeekInMonth,
          isCurrent: !!row?.isCurrentPeriod,
          isFuture: !!row?.isFuturePeriod,
          isReadOnly: !!row?.isReadOnlyPeriod,
          isRearHistoricalPeriod: !!row?.isRearHistoricalPeriod,
          isTailWeek: !!row?.isTailWeek,
          index: idx,
          row,
          values: {
            salesActual: safeNumber(row?.salesActual),
            salesPlan: safeNumber(row?.salesPlan),
            planPercent: (() => {
              // Always use the sum of visible-week plan values as denominator so
              // percents reliably sum to 100% in allocation mode after edits.
              const totals = monthTotals.get(String(row?.representativeMonthKey || `month-${idx}`)) || {}
              const monthSalesPlan = safeNumber(totals?.salesPlan)
              return monthSalesPlan > 0 ? (safeNumber(row?.salesPlan) / monthSalesPlan) * 100 : safeNumber(row?.planPercent)
            })(),
            actualPercent: (() => {
              const totals = monthTotals.get(String(row?.representativeMonthKey || `month-${idx}`)) || {}
              const monthSalesActual = safeNumber(totals?.salesActual)
              return monthSalesActual > 0 ? (safeNumber(row?.salesActual) / monthSalesActual) * 100 : safeNumber(row?.actualPercent)
            })(),
            actualInvBOM: safeNumber(row?.actualInvBOM),
            actualSS: safeNumber(row?.actualSS),
            freshness: row?.freshness ?? null,
            trendedTurn: row?.trendedTurn ?? null,
            planSS: safeNumber(row?.planSS),
            planInvBOM: safeNumber(row?.planInvBOM),
            planInflowRetail: safeNumber(row?.planInflowRetail),
            planInflowCost: safeNumber(row?.planInflowCost),
            onOrderRetailMtd: safeNumber(row?.onOrderRetailMtd),
            onOrderCostMtd: safeNumber(row?.onOrderCostMtd),
            actualInflowRetail: safeNumber(row?.actualInflowRetail),
            actualInflowCost: safeNumber(row?.actualInflowCost),
            markdownActual: safeNumber(row?.markdownActual),
            markdownPlan: safeNumber(row?.markdownPlan),
            markdownPlanPercent: (() => {
              // Always use the sum of visible-week markdown plan as denominator.
              const totals = monthTotals.get(String(row?.representativeMonthKey || `month-${idx}`)) || {}
              const monthMarkdownPlan = safeNumber(totals?.markdownPlan)
              return monthMarkdownPlan > 0 ? (safeNumber(row?.markdownPlan) / monthMarkdownPlan) * 100 : safeNumber(row?.markdownPlanPercent)
            })(),
            markdownActualPercent: (() => {
              const totals = monthTotals.get(String(row?.representativeMonthKey || `month-${idx}`)) || {}
              const monthMarkdownActual = safeNumber(totals?.markdownActual)
              return monthMarkdownActual > 0 ? (safeNumber(row?.markdownActual) / monthMarkdownActual) * 100 : safeNumber(row?.markdownActualPercent)
            })(),
          },
        }]
      }

      return splitSegments.map((segment, segmentIndex) => ({
        type: 'segment',
        displayKey: `week-${row?.period ?? idx}-segment-${segmentIndex}`,
        key: `${row?.period ?? idx}-segment-${segmentIndex}`,
        label: row?.headerLabel ?? row?.shortPeriod ?? `W${idx + 1}`,
        monthKey: String(segment?.monthKey ?? row?.representativeMonthKey ?? `month-${idx}`),
        monthLabel: segment?.monthLabel || row?.representativeMonthLabel || 'Month',
        monthDayCount: Number(segment?.days ?? 0),
        isPartialWeekInMonth: true,
        isCurrent: !!row?.isCurrentPeriod,
        isFuture: !!row?.isFuturePeriod,
        isReadOnly: !!row?.isReadOnlyPeriod,
        isRearHistoricalPeriod: !!row?.isRearHistoricalPeriod,
        isTailWeek: !!row?.isTailWeek,
        isSplitSegment: true,
        segmentIndex,
        segment,
        index: idx,
        row,
        values: {
          salesActual: buildColumnFieldValue({ row, segment, field: 'salesActual' }),
          salesPlan: buildColumnFieldValue({ row, segment, field: 'salesPlan' }),
          planPercent: (() => {
            // Always use visible-week totals so percents sum to 100%.
            const monthKey = String(segment?.monthKey || row?.representativeMonthKey || `month-${idx}`)
            const segmentSalesPlan = buildColumnFieldValue({ row, segment, field: 'salesPlan' })
            const monthSalesPlan = safeNumber(monthTotals.get(monthKey)?.salesPlan)
            return monthSalesPlan > 0 ? (safeNumber(segmentSalesPlan) / monthSalesPlan) * 100 : buildColumnFieldValue({ row, segment, field: 'planPercent' })
          })(),
          actualPercent: (() => {
            const monthKey = String(segment?.monthKey || row?.representativeMonthKey || `month-${idx}`)
            const monthSalesActual = safeNumber(monthTotals.get(monthKey)?.salesActual)
            const segmentSalesActual = buildColumnFieldValue({ row, segment, field: 'salesActual' })
            return monthSalesActual > 0 ? (safeNumber(segmentSalesActual) / monthSalesActual) * 100 : buildColumnFieldValue({ row, segment, field: 'actualPercent' })
          })(),
          actualInvBOM: buildColumnFieldValue({ row, segment, field: 'actualInvBOM' }),
          actualSS: buildColumnFieldValue({ row, segment, field: 'actualSS' }),
          freshness: buildColumnFieldValue({ row, segment, field: 'freshness' }),
          trendedTurn: buildColumnFieldValue({ row, segment, field: 'trendedTurn' }),
          planSS: buildColumnFieldValue({ row, segment, field: 'planSS' }),
          planInvBOM: buildColumnFieldValue({ row, segment, field: 'planInvBOM' }),
          planInflowRetail: buildColumnFieldValue({ row, segment, field: 'planInflowRetail' }),
          planInflowCost: buildColumnFieldValue({ row, segment, field: 'planInflowCost' }),
          onOrderRetailMtd: buildColumnFieldValue({ row, segment, field: 'onOrderRetailMtd' }),
          onOrderCostMtd: buildColumnFieldValue({ row, segment, field: 'onOrderCostMtd' }),
          actualInflowRetail: buildColumnFieldValue({ row, segment, field: 'actualInflowRetail' }),
          actualInflowCost: buildColumnFieldValue({ row, segment, field: 'actualInflowCost' }),
          markdownActual: buildColumnFieldValue({ row, segment, field: 'markdownActual' }),
          markdownPlan: buildColumnFieldValue({ row, segment, field: 'markdownPlan' }),
          markdownPlanPercent: (() => {
            // Always use visible-week totals so percents sum to 100%.
            const monthKey = String(segment?.monthKey || row?.representativeMonthKey || `month-${idx}`)
            const segmentMarkdownPlan = buildColumnFieldValue({ row, segment, field: 'markdownPlan' })
            const monthMarkdownPlan = safeNumber(monthTotals.get(monthKey)?.markdownPlan)
            return monthMarkdownPlan > 0 ? (safeNumber(segmentMarkdownPlan) / monthMarkdownPlan) * 100 : buildColumnFieldValue({ row, segment, field: 'markdownPlanPercent' })
          })(),
          markdownActualPercent: (() => {
            const monthKey = String(segment?.monthKey || row?.representativeMonthKey || `month-${idx}`)
            const monthMarkdownActual = safeNumber(monthTotals.get(monthKey)?.markdownActual)
            const segmentMarkdownActual = buildColumnFieldValue({ row, segment, field: 'markdownActual' })
            return monthMarkdownActual > 0 ? (safeNumber(segmentMarkdownActual) / monthMarkdownActual) * 100 : buildColumnFieldValue({ row, segment, field: 'markdownActualPercent' })
          })(),
        },
      }))
    })
  }, [data, monthTotals])

  const monthSubtotals = useMemo(() => buildWeeklyMonthSubtotals(data), [data])

  const allColumns = useMemo(() => {
    const lastDisplayIndexByMonth = new Map()
    baseColumns.forEach((column, displayIndex) => {
      lastDisplayIndexByMonth.set(String(column.monthKey), displayIndex)
    })

    const subtotalsByMonth = new Map(monthSubtotals.map((subtotal) => [String(subtotal?.monthKey), subtotal]))
    const out = []

    baseColumns.forEach((column, displayIndex) => {
      out.push(column)
      const monthKey = String(column.monthKey)
      const subtotal = subtotalsByMonth.get(monthKey)
      if (subtotal && lastDisplayIndexByMonth.get(monthKey) === displayIndex) {
        out.push({
          type: 'subtotal',
          displayKey: `subtotal-${monthKey}`,
          key: `subtotal-${monthKey}`,
          label: 'Subtotal',
          monthKey,
          monthLabel: subtotal?.monthLabel || column.monthLabel || 'Month',
          subtotalMonthKey: monthKey,
          subtotalMonthLabel: subtotal?.monthLabel || column.monthLabel || 'Month',
          subtotalValues: subtotal?.values || {},
          subtotalModes: subtotal?.modes || {},
          isCurrent: false,
          isFuture: false,
          index: column.index,
        })
      }
    })

    return out
  }, [baseColumns, monthSubtotals])

  const visibleMonthKeys = useMemo(() => {
    const orderedMonthKeys = []
    // Track whether a month has at least one full (non-split) week column.
    // Stub months (only partial-week segments, no full-week columns — e.g. the prior-month
    // days at the boundary before/after the editable window) are hidden by default.
    const monthHasFullWeek = new Map()
    allColumns.forEach((column) => {
      const monthKey = String(column?.monthKey || '')
      if (!monthKey) return
      if (!orderedMonthKeys.includes(monthKey)) orderedMonthKeys.push(monthKey)
      if (column.type === 'week') {
        monthHasFullWeek.set(monthKey, true)
      } else if (column.type === 'segment' && !monthHasFullWeek.has(monthKey)) {
        monthHasFullWeek.set(monthKey, false)
      }
    })

    // When "show all" is on, include every month including stubs.
    if (showAllMonthColumns) return orderedMonthKeys

    // Default view: only months that have at least one full-week column (skip stubs).
    const fullMonthKeys = orderedMonthKeys.filter((key) => monthHasFullWeek.get(key) === true)
    if (fullMonthKeys.length <= 2) return fullMonthKeys

    // More than 2 full months: show first 2 only.
    const result = []
    for (const key of orderedMonthKeys) {
      if (monthHasFullWeek.get(key) !== true) continue
      result.push(key)
      if (result.length >= 2) break
    }
    return result
  }, [allColumns, showAllMonthColumns])

  const columns = useMemo(() => {
    const visibleMonthKeySet = new Set(visibleMonthKeys)
    return allColumns.filter((column) => visibleMonthKeySet.has(String(column?.monthKey || '')))
  }, [allColumns, visibleMonthKeys])

  const hasHiddenMonthColumns = useMemo(() => {
    const orderedMonthKeys = []
    allColumns.forEach((column) => {
      const monthKey = String(column?.monthKey || '')
      if (!monthKey || orderedMonthKeys.includes(monthKey)) return
      orderedMonthKeys.push(monthKey)
    })
    return orderedMonthKeys.length > visibleMonthKeys.length
  }, [allColumns, visibleMonthKeys])

  const monthColorsByKey = useMemo(() => {
    const seen = new Map()
    let paletteIdx = 0
    columns.forEach((c) => {
      if (!seen.has(c.monthKey)) {
        seen.set(c.monthKey, monthPalette[paletteIdx % monthPalette.length])
        paletteIdx += 1
      }
    })
    return seen
  }, [columns])

  const monthGroups = useMemo(() => {
    const groups = []
    columns.forEach((c) => {
      const color = monthColorsByKey.get(c.monthKey) || '#f3f4f6'
      const title = c.type === 'subtotal'
        ? `${c.subtotalMonthLabel} subtotal`
        : c.isPartialWeekInMonth && c.monthDayCount > 0
        ? `${c.monthLabel} (${c.monthDayCount} of 7 days in month)`
        : c.monthLabel
      const last = groups[groups.length - 1]
      if (last && last.monthKey === c.monthKey) {
        last.colSpan += 1
        last.hasPartialWeek = last.hasPartialWeek || (c.type !== 'subtotal' && c.isPartialWeekInMonth)
        return
      }
      groups.push({
        monthKey: c.monthKey,
        label: c.monthLabel,
        title,
        color,
        hasPartialWeek: c.type === 'week' && c.isPartialWeekInMonth,
        colSpan: 1,
      })
    })
    return groups
  }, [columns, monthColorsByKey])

  const inventoryFreshnessDisplay = useMemo(() => {
    const rows = Array.isArray(data) ? data : []
    const selectedRow = rows.find((row) => row?.isCurrentPeriod)
      || rows.find((row) => !row?.isReadOnlyPeriod)
      || rows[0]
      || null
    return formatFreshnessPercent(selectedRow?.freshness)
  }, [data])

  const formatNumber = (v, decimals = 0) => {
    if (v === null || typeof v === 'undefined' || v === '') return '-'
    const n = Number(v)
    if (!Number.isFinite(n)) return '-'
    const d = Math.min(1, Math.max(0, Number(decimals) || 0))
    if (d > 0) return n.toFixed(d)
    return Math.round(n).toLocaleString()
  }

  const clearEditValue = (cellKey) => {
    setEditValues((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, cellKey)) return prev
      const next = { ...prev }
      delete next[cellKey]
      return next
    })
  }

  const renderCell = ({
    value,
    editable = false,
    decimals = 0,
    isCurrent = false,
    isFuture = false,
    field,
    weekIndex,
    segmentMeta = null,
    showOrangeWhenDifferent = false,
    showRedWhenBelowComparison = false,
    showRedWhenNegative = false,
    comparisonValue = null,
  }) => {
    const displayDecimals = getPlannerDisplayDecimals(field, decimals)
    const showRed = (showRedWhenNegative && Number(value ?? 0) < 0)
      || (showRedWhenBelowComparison && Number(comparisonValue ?? 0) > 0 && Number(value ?? 0) < Number(comparisonValue ?? 0))

    if (editable) {
      const cellKey = segmentMeta?.segmentIndex !== null && typeof segmentMeta?.segmentIndex !== 'undefined'
        ? `${field}-${weekIndex}-segment-${segmentMeta.segmentIndex}`
        : `${field}-${weekIndex}`
      const normalized = (value === null || typeof value === 'undefined') ? '' : value
      const display = Object.prototype.hasOwnProperty.call(editValues, cellKey)
        ? editValues[cellKey]
        : formatPlannerEditableDisplayValue(field, normalized, displayDecimals)
      return (
        <TextField
          fullWidth
          value={display}
          onFocus={() => {
            if (Object.prototype.hasOwnProperty.call(editValues, cellKey)) return
            setEditValues((prev) => ({ ...prev, [cellKey]: getPlannerEditableRawValue(field, normalized, displayDecimals) }))
          }}
          onChange={(e) => {
            // Only update local display state – the parent commit happens on blur/tab-out.
            // Calling the expensive parent onChange on every keystroke caused
            // [Violation] 'input' handler warnings AND silently broke onBlur because the
            // `value` prop was already up-to-date by the time blur fired, making
            // hasPlannerValueChanged return false.
            const raw = normalizePlannerRawInput(e.target.value)
            if (displayDecimals > 0) {
              if (!/^\d*(\.\d*)?$/.test(raw)) return
              setEditValues((prev) => ({ ...prev, [cellKey]: raw }))
              return
            }
            if (!/^\d*$/.test(raw)) return
            setEditValues((prev) => ({ ...prev, [cellKey]: raw }))
          }}
          onBlur={(e) => {
            if (!Object.prototype.hasOwnProperty.call(editValues, cellKey)) return
            const raw = normalizePlannerRawInput(e?.target?.value ?? editValues[cellKey])
            const changed = hasPlannerValueChanged(field, raw, value, displayDecimals)
            // eslint-disable-next-line no-console
            console.log('[planner][blur]', { field, weekIndex, cellKey, raw, currentValue: value, changed, segmentMeta })
            if (changed) {
              const parsed = parsePlannerEditedValue(field, raw, displayDecimals)
              if (parsed !== null) onChange?.(weekIndex, field, parsed, segmentMeta)
            }
            clearEditValue(cellKey)
          }}
          variant="outlined"
          size="small"
          type="text"
          inputProps={{
            'data-testid': segmentMeta?.segmentIndex !== null && typeof segmentMeta?.segmentIndex !== 'undefined'
              ? `weekly-input-${field}-${weekIndex}-segment-${segmentMeta.segmentIndex}`
              : `weekly-input-${field}-${weekIndex}`,
            'aria-label': segmentMeta?.segmentIndex !== null && typeof segmentMeta?.segmentIndex !== 'undefined'
              ? `weekly-${field}-${weekIndex}-segment-${segmentMeta.segmentIndex}`
              : `weekly-${field}-${weekIndex}`,
            inputMode: displayDecimals > 0 ? 'decimal' : 'numeric',
            style: {
              textAlign: 'center',
              paddingTop: 6,
              paddingBottom: 6,
              paddingLeft: 0,
              paddingRight: 0,
              fontSize: 13,
            },
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              '& input': {
                color: showRed ? '#b91c1c' : 'rgba(0, 0, 0, 0.87)',
                fontWeight: showRed ? 700 : 600,
              },
            },
            // Remove inner padding so the input can use the full cell width.
            '& .MuiOutlinedInput-input': {
              paddingLeft: '0px !important',
              paddingRight: '0px !important',
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }}
        />
      )
    }

    const showOrange = !showRed && ((isFuture && Number(value) === 0) || (showOrangeWhenDifferent && isFuture))

    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: 13,
          textAlign: 'center',
          fontWeight: showRed ? 700 : (isCurrent ? 700 : 500),
          color: showRed ? '#b91c1c' : (showOrange ? '#f97316' : 'inherit'),
        }}
      >
        {formatNumber(value, displayDecimals)}
      </Typography>
    )
  }

  const sectionHeader = (label, color, options = {}) => {
    const bg = color === 'green' ? '#d1fae5' : color === 'yellow' ? '#fef3c7' : '#fee2e2'
    const fg = color === 'green' ? '#065f46' : color === 'yellow' ? '#92400e' : '#991b1b'
    const {
      showToggle = false,
      expanded = true,
      onToggle = null,
      toggleLabel = '',
      metaLabel = null,
      metaValue = null,
      metaTestId = null,
    } = options
    return (
      <TableRow>
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: bg,
            color: fg,
            fontWeight: 700,
            fontSize: 12,
            py: 1,
            minWidth: 160,
            borderTop: '1px solid rgba(209,213,219,0.8)',
            borderBottom: '1px solid rgba(209,213,219,0.8)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.2 }}>
              <span>{label}</span>
              {metaLabel && metaValue ? (
                <Typography variant="caption" data-testid={metaTestId || undefined} sx={{ fontSize: 10, fontWeight: 700, color: fg, lineHeight: 1.1 }}>
                  {`${metaLabel} ${metaValue}`}
                </Typography>
              ) : null}
            </Box>
            {showToggle ? (
              <Tooltip title={expanded ? `Collapse ${toggleLabel}` : `Expand ${toggleLabel}`}>
                <IconButton
                  size="small"
                  aria-label={expanded ? `Collapse ${toggleLabel}` : `Expand ${toggleLabel}`}
                  onClick={onToggle}
                  sx={{
                    color: fg,
                    border: '1px solid rgba(146,64,14,0.25)',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.8)',
                    },
                  }}
                >
                  {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>
        {monthGroups.map((group, idx) => (
          <TableCell
            key={`${label}-${group.monthKey}`}
            colSpan={group.colSpan}
            align="center"
            sx={{
              backgroundColor: bg,
              color: fg,
              fontWeight: 700,
              fontSize: 12,
              py: 1,
              borderTop: '1px solid rgba(209,213,219,0.8)',
              borderBottom: '1px solid rgba(209,213,219,0.8)',
              borderLeft: idx > 0 ? '1px solid rgba(209,213,219,0.8)' : '1px solid rgba(209,213,219,0.35)',
              borderRight: '1px solid rgba(209,213,219,0.35)',
            }}
          >
          </TableCell>
        ))}
      </TableRow>
    )
  }

  const rowBg = (sectionColor) => {
    if (sectionColor === 'green') return '#ecfdf5'
    if (sectionColor === 'yellow') return '#fffbeb'
    if (sectionColor === 'red') return '#fef2f2'
    return '#fff'
  }

  const renderRow = (label, field, { editable = false, decimals = 0, showOrangeWhenDifferent = false, showRedWhenBelowComparison = false, showRedWhenNegative = false, comparisonField = null, sectionColor, copyKey = null } = {}) => {

    return (
      <TableRow>
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: rowBg(sectionColor),
            fontWeight: 600,
            fontSize: 12,
            minWidth: 160,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{label}</span>
            {copyKey && typeof onCopyActuals === 'function' ? (
              <Tooltip title="Copy Actuals to Plan">
                <Chip
                  size="small"
                  color="success"
                  icon={<ContentCopyIcon />}
                  onClick={() => onCopyActuals(copyKey)}
                  sx={{ height: 22 }}
                />
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>

        {columns.map((c, displayIdx) => {
          if (c.type === 'subtotal') {
            const subtotalValue = c?.subtotalValues?.[field]
            const hasSubtotalValue = subtotalValue !== null && typeof subtotalValue !== 'undefined' && Number.isFinite(Number(subtotalValue))

            return (
              <TableCell
                key={`${field}-${c.displayKey}`}
                data-testid={`weekly-subtotal-cell-${field}-${c.subtotalMonthKey}`}
                sx={{
                  backgroundColor: '#eef2ff',
                  color: '#312e81',
                  py: 0.5,
                  borderLeft: '2px solid rgba(99,102,241,0.35)',
                  borderRight: '2px solid rgba(99,102,241,0.35)',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                  {hasSubtotalValue ? (
                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                      {formatNumber(subtotalValue, getPlannerDisplayDecimals(field, decimals))}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                      -
                    </Typography>
                  )}
                </Box>
              </TableCell>
            )
          }

          const value = c?.values?.[field]
          return (
            <TableCell
              key={`${field}-${c.displayKey}`}
              sx={{
                  backgroundColor: c.isCurrent ? '#4c1d95' : (c.isTailWeek ? '#e0f2fe' : (c.isReadOnly ? '#f3f4f6' : rowBg(sectionColor))),
                color: c.isCurrent ? '#fff' : 'inherit',
                py: 0.5,
                borderLeft: displayIdx > 0 && columns[displayIdx - 1]?.monthKey !== c.monthKey ? `3px solid ${monthColorsByKey.get(c.monthKey) || '#d1d5db'}` : '1px solid rgba(224,224,224,0.5)',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                {renderCell({
                  value,
                  editable: editable && !c.isReadOnly && c.type !== 'subtotal' && (c.type !== 'segment' || editableFields.has(field)),
                  decimals,
                  isCurrent: c.isCurrent,
                  isFuture: c.isFuture,
                  field,
                  weekIndex: c.index,
                  segmentMeta: c.type === 'segment' ? { segmentIndex: c.segmentIndex, monthKey: c.monthKey } : null,
                  showOrangeWhenDifferent,
                  showRedWhenBelowComparison,
                  showRedWhenNegative,
                  comparisonValue: comparisonField ? c?.values?.[comparisonField] : null,
                })}
              </Box>
            </TableCell>
          )
        })}
      </TableRow>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 1 }}>
      {(hasHiddenMonthColumns || showAllMonthColumns) ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 1, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc' }}>
          <Button
            size="small"
            variant="outlined"
            data-testid="weekly-columns-visibility-toggle"
            onClick={() => setShowAllMonthColumns((prev) => !prev)}
          >
            {showAllMonthColumns ? 'Show current + next month only' : 'Show all months'}
          </Button>
        </Box>
      ) : null}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: '#f8fafc',
                  minWidth: 160,
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                Month Context
              </TableCell>
              {monthGroups.map((group) => (
                <TableCell
                  key={`month-group-${group.monthKey}`}
                  colSpan={group.colSpan}
                  align="center"
                  title={group.title}
                  sx={{
                    backgroundColor: group.color,
                    color: '#1f2937',
                    fontWeight: 700,
                    fontSize: 12,
                    borderBottom: '1px solid rgba(209,213,219,0.8)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <span>{group.label}</span>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: '#f3f4f6',
                  minWidth: 160,
                }}
              />
              {columns.map((c, displayIdx) => (
                <TableCell
                  key={c.displayKey}
                  data-testid={c.type === 'subtotal' ? `weekly-subtotal-header-${c.subtotalMonthKey}` : `weekly-column-header-${c.displayKey}`}
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    textAlign: 'center',
                    minWidth: 80,
                    backgroundColor: c.type === 'subtotal' ? '#e0e7ff' : (c.isCurrent ? '#4c1d95' : (c.isTailWeek ? '#bae6fd' : (c.isReadOnly ? '#e5e7eb' : (monthColorsByKey.get(c.monthKey) || '#f3f4f6')))),
                    color: c.isCurrent ? '#fff' : 'inherit',
                    borderLeft: c.type === 'subtotal'
                      ? '2px solid rgba(99,102,241,0.35)'
                      : displayIdx > 0 && columns[displayIdx - 1]?.monthKey !== c.monthKey
                        ? `3px solid ${monthColorsByKey.get(c.monthKey) || '#d1d5db'}`
                        : '1px solid rgba(224,224,224,0.5)',
                    borderRight: c.type === 'subtotal' ? '2px solid rgba(99,102,241,0.35)' : undefined,
                  }}
                >
                  {c.type === 'subtotal' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <Typography variant="caption" component="div" sx={{ fontSize: 9, fontWeight: 800, color: '#4338ca', lineHeight: 1.1 }}>
                        {renderMonthLabel(c.subtotalMonthLabel)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.15 }}>
                        Subtotal
                      </Typography>
                    </Box>
                  ) : c.type === 'segment' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <span>{c.label}</span>
                      <Typography variant="caption" component="div" sx={{ fontSize: 10, fontWeight: 700, color: c.isCurrent ? '#ede9fe' : '#374151', lineHeight: 1.1 }}>
                        {renderMonthLabel(c.monthLabel)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: c.isCurrent ? '#fde68a' : '#9a3412', lineHeight: 1.1 }}>
                        {`${c.monthDayCount} day${c.monthDayCount === 1 ? '' : 's'}`}
                      </Typography>
                      {c.isReadOnly ? (
                        <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: '#6b7280', lineHeight: 1.1 }}>
                          {c.isTailWeek ? 'Tail week' : 'Read only'}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <span>{c.label}</span>
                      <Typography variant="caption" component="div" sx={{ fontSize: 10, fontWeight: 600, color: c.isCurrent ? '#ede9fe' : '#4b5563' }}>
                        {renderMonthLabel(c.monthLabel)}
                      </Typography>
                      {c.isReadOnly ? (
                        <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: '#6b7280', lineHeight: 1.1 }}>
                          {c.isTailWeek ? 'Tail week' : 'Read only'}
                        </Typography>
                      ) : null}
                      {c.isPartialWeekInMonth ? (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: c.isCurrent ? '#fde68a' : '#9a3412',
                            lineHeight: 1.1,
                          }}
                        >
                          {`${c.monthDayCount} of 7`}
                        </Typography>
                      ) : null}
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sectionHeader('Sales', 'green')}
            {renderRow('Actual', 'salesActual', { sectionColor: 'green' })}
            {renderRow('Plan', 'salesPlan', { sectionColor: 'green', showOrangeWhenDifferent: true, showRedWhenBelowComparison: true, comparisonField: 'salesActual', editable: true })}
            {renderRow('Plan %', 'planPercent', { sectionColor: 'green', decimals: 1, editable: true })}
            {renderRow('Actual %', 'actualPercent', { sectionColor: 'green', decimals: 1, copyKey: 'sales' })}

            {sectionHeader('Inventory', 'yellow', {
              showToggle: true,
              expanded: inventoryOnOrderExpanded,
              onToggle: () => setInventoryOnOrderExpanded((prev) => !prev),
              toggleLabel: 'On Order rows',
              metaLabel: 'Freshness',
              metaValue: inventoryFreshnessDisplay,
              metaTestId: 'weekly-inventory-header-freshness',
            })}
            {renderRow('Actual Inv BOW', 'actualInvBOM', { sectionColor: 'yellow' })}
            {renderRow('Actual S/S', 'actualSS', { sectionColor: 'yellow', decimals: 1, copyKey: 'inventory' })}
            {renderRow('Plan S/S', 'planSS', { sectionColor: 'yellow', decimals: 1 })}
            {renderRow('Plan Inv BOW', 'planInvBOM', { sectionColor: 'yellow', editable: true })}
            {renderRow('Plan Inflow Retail', 'planInflowRetail', { sectionColor: 'yellow', showRedWhenNegative: true })}
            {renderRow('Plan Inflow Cost', 'planInflowCost', { sectionColor: 'yellow', showRedWhenNegative: true })}
            {inventoryOnOrderExpanded ? renderRow('On Order Retail MTD', 'onOrderRetailMtd', { sectionColor: 'yellow' }) : null}
            {inventoryOnOrderExpanded ? renderRow('On Order Cost MTD', 'onOrderCostMtd', { sectionColor: 'yellow' }) : null}
            {renderRow('Actual Inflow Retail', 'actualInflowRetail', { sectionColor: 'yellow', showOrangeWhenDifferent: true })}
            {renderRow('Actual Inflow Cost', 'actualInflowCost', { sectionColor: 'yellow', showOrangeWhenDifferent: true })}

            {sectionHeader('Markdowns', 'red')}
            {renderRow('Actual', 'markdownActual', { sectionColor: 'red' })}
            {renderRow('Plan', 'markdownPlan', { sectionColor: 'red', showOrangeWhenDifferent: true, editable: true })}
            {renderRow('Plan % MD', 'markdownPlanPercent', { sectionColor: 'red', decimals: 1, editable: true })}
            {renderRow('Actual % MD', 'markdownActualPercent', { sectionColor: 'red', decimals: 1, copyKey: 'markdown' })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default WeeklyPlanningGrid

